// static-report.json decoded into words (DESIGN_SPEC §12.14). Pure functions; FIG Static PE renders them.
//   peKind(machine) "PE32" | "PE32+" · machineWords(machine) · subsystemWords(subsystem)
//   characteristics(0x0122) → { hex: "0x0122", flags: [{ name: "EXECUTABLE_IMAGE", words: "executable image" }, …] }
//   sectionPerms(c) → { perms: "r-x", code, data, discardable, rwx, words }
//   entryPoint(report) → { hex: "0x000EFBAE", note }            the report has no section RVAs, so the note names the executable section(s)
//   stamp(unix, submittedMs) → { iso, flag: future | zeroed | pre-2000 | null, days, note }
//   importsOf(report) → { dlls: [{ dll, fns }], fnCount, dotnet, note? }
//   annotateStrings(strings, { hosts, ips, iocs }) → StringNote[]   interesting first; static-string IOC false positives labelled
//   PACKED = 7.2 bits per byte
import type { BehaviorReport, StaticReport, StaticSection } from "@/types";

export const PACKED = 7.2;

export const hex = (n: number, width = 8) => `0x${(n >>> 0).toString(16).toUpperCase().padStart(width, "0")}`;

const WIDE = /^(x86_64|amd64|x64|arm64|aarch64|ia64)$/i;
export const peKind = (machine: string): "PE32" | "PE32+" => (WIDE.test(machine.trim()) ? "PE32+" : "PE32");

const MACHINE_WORDS: Record<string, string> = {
  i386: "32-bit x86",
  x86_64: "64-bit x86",
  amd64: "64-bit x86",
  arm64: "64-bit ARM",
  arm: "32-bit ARM",
};
export const machineWords = (machine: string) => MACHINE_WORDS[machine.trim().toLowerCase()] ?? "unknown machine";

const SUBSYSTEM_WORDS: Record<string, string> = {
  windows_gui: "a windowed program",
  windows_cui: "a console program",
  native: "a driver or boot-time binary",
  windows_ce_gui: "Windows CE",
  efi_application: "an EFI application",
  efi_boot_service_driver: "an EFI driver",
  efi_runtime_driver: "an EFI runtime driver",
  posix_cui: "a POSIX console program",
  os2_cui: "an OS/2 console program",
};
export const subsystemWords = (sub: string) => SUBSYSTEM_WORDS[sub.trim().toLowerCase()] ?? "";

// IMAGE_FILE_* (COFF characteristics)
const CHAR_FLAGS: [number, string, string][] = [
  [0x0001, "RELOCS_STRIPPED", "relocations stripped"],
  [0x0002, "EXECUTABLE_IMAGE", "executable image"],
  [0x0004, "LINE_NUMS_STRIPPED", "line numbers stripped"],
  [0x0008, "LOCAL_SYMS_STRIPPED", "local symbols stripped"],
  [0x0010, "AGGRESSIVE_WS_TRIM", "aggressive working-set trim"],
  [0x0020, "LARGE_ADDRESS_AWARE", "large address aware"],
  [0x0080, "BYTES_REVERSED_LO", "bytes reversed (low)"],
  [0x0100, "32BIT_MACHINE", "32-bit machine"],
  [0x0200, "DEBUG_STRIPPED", "debug info stripped"],
  [0x0400, "REMOVABLE_RUN_FROM_SWAP", "runs from swap if on removable media"],
  [0x0800, "NET_RUN_FROM_SWAP", "runs from swap if on the network"],
  [0x1000, "SYSTEM", "system file"],
  [0x2000, "DLL", "DLL"],
  [0x4000, "UP_SYSTEM_ONLY", "uniprocessor only"],
  [0x8000, "BYTES_REVERSED_HI", "bytes reversed (high)"],
];

export function characteristics(c: number): { hex: string; flags: { name: string; words: string }[] } {
  const v = Number(c) >>> 0;
  return { hex: hex(v, 4), flags: CHAR_FLAGS.filter(([bit]) => (v & bit) !== 0).map(([, name, words]) => ({ name, words })) };
}

// IMAGE_SCN_*
const SCN_CODE = 0x20;
const SCN_IDATA = 0x40;
const SCN_UDATA = 0x80;
const SCN_DISCARDABLE = 0x02000000;
const SCN_EXEC = 0x20000000;
const SCN_READ = 0x40000000;
const SCN_WRITE = 0x80000000;

type Perms = { perms: string; code: boolean; data: boolean; discardable: boolean; exec: boolean; rwx: boolean; words: string };

export function sectionPerms(c: number): Perms {
  const v = Number(c) >>> 0;
  const r = (v & SCN_READ) !== 0;
  const w = (v & SCN_WRITE) !== 0;
  const x = (v & SCN_EXEC) !== 0;
  const code = (v & SCN_CODE) !== 0;
  const data = (v & (SCN_IDATA | SCN_UDATA)) !== 0;
  const discardable = (v & SCN_DISCARDABLE) !== 0;
  const words = [code ? "code" : data ? ((v & SCN_UDATA) !== 0 && (v & SCN_IDATA) === 0 ? "uninitialized data" : "data") : null, discardable ? "discardable" : null, w && x ? "writable and executable" : null]
    .filter(Boolean)
    .join(" · ");
  return { perms: `${r ? "r" : "-"}${w ? "w" : "-"}${x ? "x" : "-"}`, code, data, discardable, exec: x, rwx: w && x, words };
}

const sectionsOf = (r: Pick<StaticReport, "sections">): StaticSection[] => (Array.isArray(r.sections) ? r.sections : []);
const nameOf = (s: StaticSection) => (s.name || "").replace(/\0+$/, "") || "(unnamed)";

export function entryPoint(r: Pick<StaticReport, "entry_point_rva" | "sections" | "is_dll">): { hex: string; note: string } {
  const ep = Number(r.entry_point_rva) >>> 0;
  if (ep === 0) return { hex: hex(0), note: r.is_dll ? "none · DLL without an entry point" : "zero · no entry point" };
  const exec = sectionsOf(r).filter((s) => sectionPerms(s.characteristics).exec);
  const note =
    exec.length === 0
      ? "no section is marked executable"
      : exec.length === 1
        ? `${nameOf(exec[0])} is the only executable section`
        : `executable sections: ${exec.map(nameOf).join(", ")}`;
  return { hex: hex(ep), note };
}

const DAY = 86_400_000;
const Y2000 = Date.UTC(2000, 0, 1);

type Stamp = { iso: string | null; flag: "future" | "zeroed" | "pre-2000" | null; days: number | null; note: string };

/** The COFF TimeDateStamp: future-dated or pre-2000 stamps are usually a reproducible-build hash, not a date. */
export function stamp(unix: number, submittedMs?: number): Stamp {
  const s = Number(unix);
  if (!Number.isFinite(s) || s === 0) return { iso: null, flag: "zeroed", days: null, note: "zeroed" };
  const ms = s * 1000;
  const iso = new Date(ms).toISOString();
  const ref = submittedMs !== undefined && Number.isFinite(submittedMs) ? submittedMs : undefined;
  const days = ref !== undefined ? Math.round((ref - ms) / DAY) : null;
  if (ref !== undefined && ms > ref + DAY)
    return { iso, flag: "future", days, note: `${Math.round((ms - ref) / DAY).toLocaleString("en-US")} days after submission · likely a build hash` };
  if (ms < Y2000) return { iso, flag: "pre-2000", days, note: "before 2000 · likely a build hash" };
  if (days === null) return { iso, flag: null, days, note: "" };
  return { iso, flag: null, days, note: days <= 0 ? "the day of submission" : `${days.toLocaleString("en-US")} ${days === 1 ? "day" : "days"} before submission` };
}

type ImportInfo = { dlls: { dll: string; fns: string[] }[]; fnCount: number; dotnet: boolean; note?: string };

export function importsOf(r: Pick<StaticReport, "imports">): ImportInfo {
  const raw = r.imports && typeof r.imports === "object" ? r.imports : {};
  const dlls = Object.entries(raw)
    .map(([dll, fns]) => ({ dll: dll.toLowerCase(), fns: Array.isArray(fns) ? fns.filter((f) => typeof f === "string") : [] }))
    .sort((a, b) => b.fns.length - a.fns.length || a.dll.localeCompare(b.dll));
  const fnCount = dlls.reduce((n, d) => n + d.fns.length, 0);
  const core = dlls.find((d) => d.dll === "mscoree.dll");
  const dotnet = !!core;
  let note: string | undefined;
  if (core && dlls.length === 1 && core.fns.every((f) => /^_Cor(Exe|Dll)Main$/.test(f)))
    note = ".NET loader stub only";
  else if (dlls.length === 0) note = "no import table · resolved at run time or packed";
  else if (fnCount <= 4 && dlls.some((d) => d.fns.some((f) => /^(LoadLibrary|GetProcAddress)/.test(f))))
    note = "loader imports only";
  return { dlls, fnCount, dotnet, note };
}

// ---- strings -------------------------------------------------------------------------------------------------
type StringKind = "family" | "pdb" | "url" | "host" | "ip" | "api" | "namespace" | "constant" | "hex" | "entropy" | "compiler" | "type" | "plain";
export type StringNote = {
  s: string;
  kind: StringKind;
  why: string;
  /** 1 = most interesting … 5 = noise; < 5 is "of interest". */
  rank: number;
  /** listed in behavior-report.json's IOCs from the static strings */
  ioc?: { kind: string; falsePositive: boolean };
};

const API_GROUPS: [RegExp, string][] = [
  [/^(CreateRemoteThread(Ex)?|VirtualAllocEx|WriteProcessMemory|NtUnmapViewOfSection|ZwUnmapViewOfSection|QueueUserAPC|NtQueueApcThread|SetThreadContext|ResumeThread|RtlCreateUserThread|NtCreateThreadEx|SetWindowsHookExA?W?)$/i, "api · injection"],
  [/^(FromBase64String|CryptAcquireContext[AW]?|CryptDecrypt|CryptStringToBinary[AW]?|RtlDecompressBuffer|BCryptDecrypt)$/i, "api · decoding"],
  [/^(LoadLibrary(Ex)?[AW]?|GetProcAddress|LdrLoadDll|LdrGetProcedureAddress)$/i, "api · resolves imports at run time"],
  [/^(CreateService[AW]?|OpenSCManager[AW]?|StartService[AW]?)$/i, "api · services"],
  [/^(RegSetValueEx[AW]?|RegCreateKeyEx[AW]?)$/i, "api · registry"],
  [/^(CreateFileMapping[AW]?|MapViewOfFile(Ex)?)$/i, "api · memory mapping"],
  [/^(Sys)?(Nt|Zw)Query(SystemInformation|InformationProcess)\d*$/i, "api · process and system discovery"],
  [/^(QueryFullProcessImageName[AW]?|GetModuleFileName(Ex)?[AW]?)$/i, "api · process discovery"],
  [/^(IsDebuggerPresent|CheckRemoteDebuggerPresent|NtQueryInformationThread)$/i, "api · debugger checks"],
  [/^(GetWindowText(Length)?[AW]?|GetForegroundWindow|GetAsyncKeyState|GetKeyState)$/i, "api · window and keyboard"],
  [/^(URLDownloadToFile[AW]?|InternetOpen(Url)?[AW]?|WinHttpOpen)$/i, "api · download"],
];
const CONSTANTS: [RegExp, string, number][] = [
  [/^PROCESS_CREATE_THREAD$/, "access right · thread creation in another process", 2],
  [/^PROCESS_VM_(WRITE|OPERATION)$/, "access right · writes another process's memory", 2],
  [/^IMAGE_(OPTIONAL|NT|FILE|DOS)_HEADER(32|64)?$/, "PE structure · parses executables itself", 2],
  [/^DESKTOP_JOURNAL(RECORD|PLAYBACK)$/, "constant · input journaling hooks", 3],
  [/^DOMAIN_VISIBLE_PASSWORD$/, "constant · Windows credential flag", 3],
  [/^DWMWA_[A-Z_]+$/, "constant · window composition", 5],
];
/** Namespaces of known commodity families. A hint for the analyst, never a verdict. */
const FAMILIES: [RegExp, string][] = [
  [/^Pulsar\./, "Pulsar"],
  [/^Quasar\./, "Quasar"],
  [/^AsyncRAT|^AsyncClient\./, "AsyncRAT"],
  [/^DcRat\./, "DcRat"],
  [/^Venom(RAT)?\./, "VenomRAT"],
  [/^xClient\./, "Quasar"],
  [/^Stub\.(Program|Client)/, "njRAT-style stub"],
  [/^RedLine\./, "RedLine"],
  [/^AgentTesla/, "Agent Tesla"],
];
const TLDS = new Set(
  "com net org io info biz co dev app xyz top ru cn de uk fr nl eu us me tk ml ga cf gq pw cc tv ws su in br jp kr ir pl it es ca au ch se no fi cz sk ua by kz example test invalid local onion online site live link club shop cloud space tech".split(" "),
);

const isNamespace = (s: string) => /^[A-Z_][A-Za-z0-9_]*(\.[A-Z_][A-Za-z0-9_]*)+$/.test(s);
const looksHost = (s: string) => {
  const m = /^(?=.{4,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+([a-z]{2,24})\.?$/i.exec(s);
  return !!m && TLDS.has(m[2].toLowerCase()) && s === s.toLowerCase();
};
const looksIp = (s: string) => /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/.test(s);

/** Mostly dictionary-shaped words (ProcessImageFileNameWin32), not random characters. */
function readable(s: string): boolean {
  const words = s.match(/[A-Z][a-z]{2,}|[a-z]{4,}/g) ?? [];
  return words.reduce((n, w) => n + w.length, 0) / s.length >= 0.6;
}

/** Shannon entropy in bits per character. */
export function charEntropy(s: string): number {
  if (!s) return 0;
  const counts = new Map<string, number>();
  for (const ch of s) counts.set(ch, (counts.get(ch) ?? 0) + 1);
  let h = 0;
  for (const c of counts.values()) {
    const p = c / s.length;
    h -= p * Math.log2(p);
  }
  return h;
}

function hostOfUrl(s: string): string | null {
  try {
    return new URL(s).hostname.toLowerCase();
  } catch {
    return null;
  }
}

type IocIndex = Map<string, string>; // lowercase value → ioc kind (static-string sourced)

function originalCase(iocs: BehaviorReport["iocs"] | undefined, lower: string): string {
  for (const list of Object.values(iocs ?? {})) for (const e of list ?? []) if (String(e.value).toLowerCase() === lower) return String(e.value);
  return lower;
}

function staticIocs(iocs: BehaviorReport["iocs"] | undefined): IocIndex {
  const m: IocIndex = new Map();
  for (const [kind, list] of Object.entries(iocs ?? {})) {
    for (const e of list ?? []) {
      const src = e.sources ?? [];
      if (src.includes("static.strings")) m.set(String(e.value).toLowerCase(), kind);
    }
  }
  return m;
}

export function annotateString(s: string, seen: { hosts: ReadonlySet<string>; ips: ReadonlySet<string>; iocs: IocIndex }): StringNote {
  const t = s.trim();
  const ioc = seen.iocs.get(t.toLowerCase());
  const fam = FAMILIES.find(([re]) => re.test(t));
  if (fam) return { s, kind: "family", why: `namespace · ${fam[1]} family hint`, rank: 1, ioc: ioc ? { kind: ioc, falsePositive: true } : undefined };
  if (/\.pdb$/i.test(t)) return { s, kind: "pdb", why: "debug path · names the build machine", rank: 1 };
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(t)) {
    const h = hostOfUrl(t);
    const hit = !!h && seen.hosts.has(h);
    return { s, kind: "url", why: hit ? "url · its host was resolved at run time" : "url · not contacted", rank: hit ? 1 : 2, ioc: ioc ? { kind: ioc, falsePositive: false } : undefined };
  }
  if (looksIp(t)) {
    const hit = seen.ips.has(t);
    return { s, kind: "ip", why: hit ? "address · contacted at run time" : "address · not contacted", rank: hit ? 1 : 2, ioc: ioc ? { kind: ioc, falsePositive: false } : undefined };
  }
  for (const [re, why] of API_GROUPS) {
    if (re.test(t)) return { s, kind: "api", why, rank: why === "api · injection" ? 1 : 2 };
  }
  for (const [re, why, rank] of CONSTANTS) {
    if (re.test(t)) return { s, kind: "constant", why, rank };
  }
  if (looksHost(t)) {
    const hit = seen.hosts.has(t.toLowerCase().replace(/\.$/, ""));
    return { s, kind: "host", why: hit ? "host · resolved at run time" : "host · never resolved", rank: hit ? 1 : 2, ioc: ioc ? { kind: ioc, falsePositive: false } : undefined };
  }
  if (isNamespace(t)) {
    if (ioc) return { s, kind: "namespace", why: `namespace, not a host · listed as a ${ioc} IOC`, rank: 2, ioc: { kind: ioc, falsePositive: true } };
    return { s, kind: "namespace", why: "namespace", rank: 4 };
  }
  if (/^__StaticArrayInitTypeSize=\d+$|^<PrivateImplementationDetails>|^<Module>$/.test(t)) return { s, kind: "compiler", why: ".NET compiler artifact", rank: 5 };
  if (/`\d+$/.test(t)) return { s, kind: "type", why: ".NET generic type", rank: 5 };
  if (/^[0-9a-f]+$/i.test(t) && (t.length === 32 || t.length === 40 || t.length === 64)) {
    const bits = t.length * 4;
    return { s, kind: "hex", why: `${t.length} hex digits · ${bits === 256 ? "a SHA-256-sized hash or key" : bits === 160 ? "SHA-1-sized" : "MD5-sized"}`, rank: 3 };
  }
  if (t.length >= 24 && /^[A-Za-z0-9+/=_~^-]+$/.test(t) && charEntropy(t) >= 4 && !readable(t)) return { s, kind: "entropy", why: "high-entropy string", rank: 3 };
  if (ioc) return { s, kind: "plain", why: `listed as a ${ioc} IOC`, rank: 2, ioc: { kind: ioc, falsePositive: false } };
  return { s, kind: "plain", why: "", rank: 5 };
}

/** Annotated strings, interesting first (rank, then the report's order). */
export function annotateStrings(
  strings: readonly string[] | null | undefined,
  ctx: { hosts?: Iterable<string>; ips?: Iterable<string>; iocs?: BehaviorReport["iocs"] } = {},
): StringNote[] {
  const seen = {
    hosts: new Set([...(ctx.hosts ?? [])].map((h) => h.toLowerCase().replace(/\.$/, ""))),
    ips: new Set(ctx.ips ?? []),
    iocs: staticIocs(ctx.iocs),
  };
  const listed = (strings ?? []).filter((x): x is string => typeof x === "string" && x.trim() !== "");
  const have = new Set(listed.map((x) => x.trim().toLowerCase()));
  // The IOC pass reads every string; the report keeps 256. Values it flagged from strings outside that cut still get a row.
  const extra = [...seen.iocs.keys()].filter((v) => !have.has(v)).map((v) => originalCase(ctx.iocs, v));
  const uniq = [...new Set([...listed, ...extra])];
  return uniq
    .map((s, i) => ({ n: annotateString(s, seen), i }))
    .sort((a, b) => a.n.rank - b.n.rank || a.i - b.i)
    .map((x) => x.n);
}
